import type { Affinity } from "@/types/affinities";
import { apiUrl } from "@/lib/api/client";
import { adminErrorMessage } from "@/hooks/admin/adminErrorMessage";

export type { Affinity };

export type AffinitiesResponse = {
  data: Affinity[];
};

export type AffinityRevenueResponse = {
  data: Array<{
    year: number;
    month: number;
    totalRevenue: number;
    revenueShare: number;
    platformShare: number;
    currency: string;
    transactionCount: number;
  }>;
};

export type AffinityUsersResponse = {
  data: Array<{
    userId: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    visitedAt: string;
    convertedAt: string | null;
    subscriptionId: string | null;
    createdAt: string;
    hasRevenue: boolean;
  }>;
};

export type AffinityReportResponse = {
  affinity: Affinity;
  totalVisitors: number;
  totalConversions: number;
  conversionRate: number;
  totalRevenue: number;
  averageRevenuePerUser: number;
  averageRevenuePerConvertedUser: number;
  monthlyBreakdown: Array<{
    year: number;
    month: number;
    totalRevenue: number;
    revenueShare: number;
    platformShare: number;
    currency: string;
    transactionCount: number;
  }>;
};

export type PaymentFormDataResponse = {
  affinity: {
    id: string;
    name: string;
    partnerName: string;
    partnerEmail: string;
    revenueSharePercent: number;
  };
  period: {
    year: number;
    month: number;
    startDate: string;
    endDate: string;
  };
  revenue: {
    total: number;
    revenueShare: number;
    platformShare: number;
    currency: string;
  };
  userStats: {
    totalVisitors: number;
    newVisitors: number;
    totalConversions: number;
    newConversions: number;
  };
  transactions: Array<{
    date: string;
    amount: number;
    subscriptionId: string;
  }>;
};

export type AffinityTransaction = {
  id: string;
  date: string;
  amount: number;
  revenueShare: number;
  platformShare: number;
  currency: string;
  subscriptionId: string | null;
  invoiceId: string | null;
  periodStart: string;
  periodEnd: string;
};

export type AffinityTransactionsResponse = {
  data: AffinityTransaction[];
};

export type CreateAffinityData = {
  code: string;
  name: string;
  partnerName: string;
  partnerEmail: string;
  revenueSharePercent: number;
  bannerText: string;
  productId: string;
  planIds: string[];
  couponId?: string | null;
  imageUrl?: string | null;
  imageKey?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  trialDurationDays?: number | null;
};

export type UpdateAffinityData = {
  name?: string;
  partnerName?: string;
  partnerEmail?: string;
  revenueSharePercent?: number;
  bannerText?: string;
  productId?: string | null;
  planIds?: string[];
  couponId?: string | null;
  isActive?: boolean;
  imageUrl?: string | null;
  imageKey?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  trialDurationDays?: number | null;
};

/**
 * Get all affinities
 */
export const getAffinities = async (params?: {
  isActive?: boolean;
}): Promise<AffinitiesResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.isActive !== undefined) {
    queryParams.append("isActive", params.isActive.toString());
  }

  const response = await fetch(apiUrl(`/api/v1/admin/affinities?${queryParams.toString()}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinities"));
  }

  return response.json();
};

/**
 * Get affinity by ID
 */
export const getAffinityById = async (id: string): Promise<Affinity> => {
  const response = await fetch(apiUrl(`/api/v1/admin/affinities/${id}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinity"));
  }

  return response.json();
};

/**
 * Get affinity by code (public)
 */
export const getAffinityByCode = async (code: string): Promise<Affinity | null> => {
  const response = await fetch(apiUrl(`/api/v1/affinities/${code}`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinity"));
  }

  return response.json();
};

/**
 * Create new affinity
 */
export const createAffinity = async (data: CreateAffinityData): Promise<Affinity> => {
  const response = await fetch(apiUrl("/api/v1/admin/affinities"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to create affinity"));
  }

  return response.json();
};

/**
 * Update affinity
 */
export const updateAffinity = async (
  id: string,
  data: UpdateAffinityData
): Promise<Affinity> => {
  const response = await fetch(apiUrl(`/api/v1/admin/affinities/${id}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to update affinity"));
  }

  return response.json();
};

/**
 * Delete affinity
 */
export const deleteAffinity = async (id: string): Promise<void> => {
  const response = await fetch(apiUrl(`/api/v1/admin/affinities/${id}`), {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to delete affinity"));
  }
};

/**
 * Get affinity revenue by month
 */
export const getAffinityRevenue = async (
  affinityId: string
): Promise<AffinityRevenueResponse> => {
  const response = await fetch(apiUrl(`/api/v1/admin/affinities/${affinityId}/revenue`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinity revenue"));
  }

  return response.json();
};

/**
 * Get affinity report
 */
export const getAffinityReport = async (
  affinityId: string
): Promise<AffinityReportResponse> => {
  const response = await fetch(apiUrl(`/api/v1/admin/affinities/${affinityId}/report`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinity report"));
  }

  return response.json();
};

/**
 * Get payment form data for a specific month
 */
export const getPaymentFormData = async (
  affinityId: string,
  year: number,
  month: number
): Promise<PaymentFormDataResponse> => {
  const response = await fetch(
    apiUrl(`/api/v1/admin/affinities/${affinityId}/payment-form/${year}/${month}`),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch payment form data"));
  }

  return response.json();
};

/**
 * Link user to affinity (create user_affinities record)
 */
export const linkUserToAffinity = async (
  userId: string,
  affinityId: string
): Promise<void> => {
  const response = await fetch(apiUrl("/api/v1/affinities/link-user"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ userId, affinityId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to link user to affinity"));
  }
};

/**
 * Mark user as converted
 * @param userId - User ID
 * @param affinityId - Affinity ID
 * @param subscriptionId - Subscription ID or payment intent ID (for one-time payments). Can be null.
 */
export const markUserConverted = async (
  userId: string,
  affinityId: string,
  subscriptionId: string | null
): Promise<void> => {
  const response = await fetch(apiUrl("/api/v1/affinities/mark-converted"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ userId, affinityId, subscriptionId }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to mark user as converted"));
  }
};

/**
 * Get users for an affinity
 */
export const getAffinityUsers = async (
  affinityId: string
): Promise<AffinityUsersResponse> => {
  const response = await fetch(apiUrl(`/api/v1/admin/affinities/${affinityId}/users`), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinity users"));
  }

  return response.json();
};

/**
 * Get transactions for a specific month
 */
export const getAffinityTransactions = async (
  affinityId: string,
  year: number,
  month: number
): Promise<AffinityTransactionsResponse> => {
  const response = await fetch(
    apiUrl(`/api/v1/admin/affinities/${affinityId}/transactions/${year}/${month}`),
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(adminErrorMessage(errorData, "Failed to fetch affinity transactions"));
  }

  return response.json();
};
